/* eslint-disable import/no-cycle */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useParams, useLocation } from 'react-router-dom';
import { Loader } from '@sdir/sds';
import styled from 'styled-components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { AttachmentType } from '@sdir/httpclient/lib/clients/aps/application';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import NewApplicationLayout from '../../NewApplicationLayout';
import { routes } from '../../../../constants/routes';
import { AddedSeagoing, Loadable } from '../../../../types';
import { countryApi, occupationRegisterApi } from '../../../../httpclient';
import { Card, InputError } from '../../../Atoms';
import { FileGroup, SubmitSequenceSummary } from '../../../Molecules';
import {
  useCurrentApplication,
  useSubmitSequence,
  useAttachmentGroupWorkspace,
  useAddedSeagoing,
} from '../../../../services/hooks';
import {
  buildSubmitSeagoingSequence,
  restoreAddedSeagoingState,
  SubmitSeagoingTracker,
} from '../../../../helpers/seagoingApiHelpers';
import { findQualification } from '../../../../helpers/Qualifications';
import { SeagoingEditor } from './SeagoingEditor';
import EditApplicationGuard from '../../../Molecules/EditApplicationGuard/EditApplicationGuard';
import { useNavigateWithLang } from '../../../../services/hooks/useNavigateWithLang';

export const AddSeagoing: FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const { attachmentGroup, applicationUid } = useParams<{
    attachmentGroup: string;
    applicationUid: string;
  }>();
  const { pathname, search } = useLocation();
  const editingExistingQualification = pathname.includes('/editseagoing');
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const fromMissingDocumentation = searchParams.get('from') === 'missingdocumentation';

  const { application, refresh, qualifications } = useCurrentApplication({
    includeQualifications: true,
  });
  const { added: existingSeagoing, refresh: refreshSeagoing } = useAddedSeagoing(attachmentGroup);
  const { submit, results, isSubmitting } = useSubmitSequence(buildSubmitSeagoingSequence);

  const {
    activeFile,
    activeFileOption,
    availableFiles,
    changes: attachmentGroupChanges,
    groupQualificationUri,
    handleFileSelected,
    handleAddNewFiles,
    handleRemoveActiveFile,
  } = useAttachmentGroupWorkspace(attachmentGroup || '');

  const [showValidation, setShowValidation] = useState(false);

  const [seagoing, setSeagoing] = useState<Loadable<AddedSeagoing[]>>({
    loading: true,
  });
  const [submitTracker, setSubmitTracker] = useState<SubmitSeagoingTracker>({});

  const qualification = useMemo(() => {
    if (!qualifications.result || !groupQualificationUri) return null;
    return findQualification(qualifications.result, groupQualificationUri);
  }, [qualifications.result, groupQualificationUri]);

  const handleExit = useCallback(() => {
    const route = fromMissingDocumentation
      ? routes.application.uploadMissingDocumentation
      : routes.application.qualification;
    navigate(generatePath(route, { applicationUid }));
  }, [navigate, applicationUid, fromMissingDocumentation]);

  const {
    result: occupations = [],
    errorObject: occupationsError,
    loading: occupationsLoading,
  } = useApi({
    apiFunction: () => occupationRegisterApi.v1OccupationregisterGetregisterinternalGet(),
    callApiOnInit: true,
  });

  const {
    result: countriesResults,
    errorObject: countriesError,
    loading: countriesLoading,
  } = useApi({
    apiFunction: () => countryApi.v1CountryGetallcountriesGet(),
    callApiOnInit: true,
  });

  const hasGroupFiles = !!availableFiles.length;

  const addedSeagoingIsValid = useMemo(() => {
    return (
      !!seagoing.result &&
      !!seagoing.result.length &&
      !seagoing.result.find((x) => !x.details?.length)
    );
  }, [seagoing]);

  const handleSeagoingChange = useCallback(
    (newSeagoing: AddedSeagoing[]) => setSeagoing({ result: newSeagoing }),
    []
  );

  const handleSave = useCallback(async () => {
    if (
      !application.result ||
      !existingSeagoing.result ||
      !seagoing.result ||
      !addedSeagoingIsValid
    ) {
      setShowValidation(true);
      return;
    }
    const { updatedAttachments, removedAttachments, newFiles } = attachmentGroupChanges;
    const { tracker: newTracker } = await submit(
      {
        seagoingGroups: seagoing.result,
        application: application.result,
        existing: existingSeagoing.result,
        attachmentGroup: attachmentGroup || null,
        qualificationUri: groupQualificationUri,
        updatedAttachments,
        removedAttachments,
        newAttachments: newFiles.map((f) => ({ file: f, type: AttachmentType.SeagoingTime })),
      },
      submitTracker
    );

    if (newTracker.errors) {
      setSubmitTracker(newTracker);
    } else {
      refresh?.();
      refreshSeagoing();

      if (editingExistingQualification) {
        navigate(
          generatePath(routes.application.uploadMissingDocumentation, {
            applicationUid,
          })
        );
      } else {
        handleExit();
      }
    }
  }, [
    application.result,
    existingSeagoing.result,
    seagoing.result,
    addedSeagoingIsValid,
    attachmentGroupChanges,
    submit,
    attachmentGroup,
    groupQualificationUri,
    submitTracker,
    refresh,
    refreshSeagoing,
    handleExit,
    applicationUid,
    editingExistingQualification,
    navigate,
  ]);

  useEffect(() => {
    if (!existingSeagoing.result) return;
    setSeagoing({ loading: true });
    restoreAddedSeagoingState(existingSeagoing.result)
      .then((restored) => {
        setSeagoing({ result: restored });
      })
      .catch((e) => {
        setSeagoing({ error: e });
      });
  }, [existingSeagoing, intl]);

  const isLoading =
    application.loading ||
    existingSeagoing.loading ||
    seagoing.loading ||
    countriesLoading ||
    occupationsLoading;

  return (
    <EditApplicationGuard>
      <NewApplicationLayout
        hideHeading={editingExistingQualification}
        exitLabel={intl.formatMessage({ id: 'general.form.back' })}
        mainContentMaxWidth="100%"
        canNavBack
        canNavNext={!isSubmitting}
        navBackLabel={intl.formatMessage({ id: 'general.form.cancel' })}
        navNextLabel={intl.formatMessage({
          id: editingExistingQualification ? 'general.form.save' : 'general.form.next',
        })}
        onNavNextClick={handleSave}
        onNavBackClick={() => navigate(-1)}
      >
        <SeagoingFormContent>
          <div>
            <SeagoingFormCard heading={intl.formatMessage({ id: 'addseagoing.heading' })}>
              <p>{intl.formatMessage({ id: 'addseagoing.description' })}</p>
              {qualification && (
                <p>
                  {qualification.title}: {qualification.description}
                </p>
              )}
              {isLoading && <Loader />}
              {!!occupations && !!countriesResults && !!seagoing.result && (
                <SeagoingEditor
                  countries={countriesResults?.source || []}
                  occupations={occupations || []}
                  seagoing={seagoing.result}
                  onSeagoingChange={handleSeagoingChange}
                />
              )}
            </SeagoingFormCard>
            {seagoing.error && <ErrorMessage error={seagoing.error} />}
            {showValidation && !availableFiles.length && (
              <ErrorMessage
                error={intl.formatMessage({ id: 'app.requirements.errors.attachmentmissing' })}
              />
            )}
            {showValidation && !addedSeagoingIsValid && (
              <ErrorMessage
                error={intl.formatMessage({ id: 'addseagoing.errors.noseagoingtimeadded' })}
              />
            )}
            {countriesError && <ErrorMessage error={countriesError} />}
            {occupationsError && <ErrorMessage error={occupationsError} />}
          </div>
          <div>
            {application.loading ? (
              <Loader />
            ) : (
              <FileGroup
                selectedFile={activeFile}
                selectedFileOption={activeFileOption}
                availableFiles={availableFiles}
                onSelectFile={handleFileSelected}
                onNewFilesAdded={handleAddNewFiles}
                onDeleteSelectedFile={handleRemoveActiveFile}
              />
            )}
            {activeFile?.error && (
              <ErrorMessage
                title={intl.formatMessage({ id: 'general.error.failedtoloadattachment' })}
                error={activeFile.error}
              />
            )}
            {!hasGroupFiles && showValidation && (
              <InputError>{intl.formatMessage({ id: 'general.form.error' })}</InputError>
            )}
          </div>
        </SeagoingFormContent>
        {isSubmitting && <Loader />}
        <SubmitSequenceSummary results={results} />
      </NewApplicationLayout>
    </EditApplicationGuard>
  );
};

const SeagoingFormContent = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  grid-row-gap: 3rem;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 32px;
  margin-top: 3rem;
  justify-content: stretch;

  > * {
    position: relative;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    grid-template-columns: 1fr;

    > div:first-child {
      min-height: 0;
    }

    > div:nth-child(2) {
      min-height: 500px;
    }
  }
`;

const SeagoingFormCard = styled(Card)`
  overflow-y: auto;
  overflow-x: hidden;
`;
